// * React
import { useState } from 'react';
// * Redux
import { useDispatch, useSelector } from 'react-redux';

// internationalization
// * i18n
import { useTranslation } from 'react-i18next';

// tanstack
// * Router
import { useNavigate } from '@tanstack/react-router';

// material ui icons
// * Icons
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

// material ui
// * MUI
import { Box, Chip, Tooltip, Typography } from '@mui/material';
// * MUIX
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import { useGetV2AlarmTypesQuery } from '@/api/generated/recalc/endpoints/v2/alarmTypes';
// * API
import { useDeleteAlarmGroupMutation } from '@/api/recalc/endpoints/alarmGroupsApiSlice';

// shared components
// * Components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
// * Hooks
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DeleteAlarmGroupDialog from './DeleteAlarmGroupDialog';

const AlarmGroupDataGrid = ({ alarmGroups, userOptions }) => {
  const localeText = useDatagridLocaleText();
  const { t } = useTranslation('alarms');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedAccount = useSelector(getSelectedAccount);

  const { data: alarmTypes = [] } = useGetV2AlarmTypesQuery({
    orderBy: 'name',
    orderDirection: 'asc',
  });

  const [deleteAlarmGroup] = useDeleteAlarmGroupMutation();

  // Local state for delete confirmation
  const [groupToDelete, setGroupToDelete] = useState(null);

  const handleConfirmDeleteAlarmGroup = async () => {
    if (!groupToDelete) return;

    const result = await deleteAlarmGroup(groupToDelete.AlarmGroupId);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message:
            t('messages.alarm_group_deleted') ||
            'Alarm group deleted successfully',
          severity: 'success',
        }),
      );
      navigate({ to: '..' });
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleEditAlarmGroup = (group) => {
    console.log('Edit alarm group:', group);
    // Implement navigation to edit page
    navigate({
      to: `/$accountId/alarms/alarm-groups/${group.AlarmGroupId}`,
    });
  };

  const handleDeleteAlarmGroup = (alarmGroup) => {
    console.log('Delete alarm group:', alarmGroup);
    setGroupToDelete(alarmGroup);
  };

  const handleCancelDeleteAlarmGroup = () => {
    setGroupToDelete(null);
  };

  return (
    <>
      <DataGridPremium
        rows={alarmGroups}
        getRowId={(row) => row.AlarmGroupId}
        columns={[
          {
            field: 'Name',
            headerName: 'Name',
            flex: 1,
          },
          {
            field: 'users',
            headerName: 'Users',
            flex: 1,
            valueGetter: (params) =>
              params?.row.AlarmSubscriptions?.length || 0,
            renderCell: (params) => {
              const userCount = params.row.AlarmSubscriptions?.length || 0;

              // Build user list for tooltip
              const usersList =
                params.row.AlarmSubscriptions?.map((sub) => {
                  const user = userOptions.find(
                    (u) => u.User.UserId === sub.User.UserId,
                  );
                  return (
                    user?.User?.Email ||
                    t('alarmGroups.unknownUser', { ns: 'alarms' })
                  );
                }).join('\n') || t('alarmGroups.noUsers', { ns: 'alarms' });

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: '100%',
                  }}
                >
                  <Tooltip
                    title={
                      <Box sx={{ whiteSpace: 'pre-line' }}>
                        <Typography variant="caption" fontWeight={600}>
                          {t('alarmGroups.users')}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 0.5 }}
                        >
                          {usersList}
                        </Typography>
                      </Box>
                    }
                    placement="bottom"
                  >
                    <Chip
                      icon={<GroupRoundedIcon />}
                      label={
                        <Typography variant="body2">
                          {userCount} {userCount === 1 ? 'user' : 'users'}
                        </Typography>
                      }
                      size="small"
                      variant={userCount > 0 ? 'filled' : 'outlined'}
                      sx={{
                        borderRadius: 1.5,
                        border: userCount > 0 ? 'none' : '1px dashed',
                        borderColor:
                          userCount > 0 ? 'primary.main' : 'text.disabled',
                        '&:hover': {
                          filter: 'brightness(0.95)',
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            field: 'alarmTypes',
            headerName: 'Alarm Types',
            flex: 1,
            renderCell: (params) => {
              const alarmTypeCount = params.row.AlarmInclusions?.length || 0;

              // Build alarm types list for tooltip
              const alarmTypesList =
                params.row.AlarmInclusions?.map(
                  (inclusion) => inclusion.AlarmType.Name,
                ).join('\n') || t('alarmGroups.noAlarmTypes', { ns: 'alarms' });

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: '100%',
                  }}
                >
                  <Tooltip
                    title={
                      <Box sx={{ whiteSpace: 'pre-line' }}>
                        <Typography variant="caption" fontWeight={600}>
                          {t('alarmGroups.alarmTypes', { ns: 'alarms' })}:
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 0.5 }}
                        >
                          {alarmTypesList}
                        </Typography>
                      </Box>
                    }
                    placement="bottom"
                  >
                    <Chip
                      icon={<NotificationsActiveRoundedIcon />}
                      label={
                        <Typography variant="body2">
                          {alarmTypeCount}{' '}
                          {alarmTypeCount === 1 ? 'alarm type' : 'alarm types'}
                        </Typography>
                      }
                      variant={alarmTypeCount > 0 ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        border: alarmTypeCount > 0 ? 'none' : '1px dashed',
                        borderRadius: 1.5,
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            field: 'actions',
            headerName: 'Actions',
            renderCell: (params) => (
              <RowActions
                row={params.row}
                //   onView={() => handleViewGateway(params.row)}
                onEdit={() => handleEditAlarmGroup(params.row)}
                onDelete={() => handleDeleteAlarmGroup(params.row)}
              />
            ),
          },
        ]}
        showToolbar
        localeText={localeText}
        slots={{
          toolbar: DataGridToolbar,
        }}
        hideFooter
        autoHeight
      />
      <DeleteAlarmGroupDialog
        open={!!groupToDelete}
        groupToDelete={groupToDelete}
        users={selectedAccount?.UserRoles || []}
        alarmTypes={alarmTypes}
        onConfirm={handleConfirmDeleteAlarmGroup}
        onCancel={handleCancelDeleteAlarmGroup}
      />
    </>
  );
};

export default AlarmGroupDataGrid;
